// greetingQueue.js — "¿este saludo ya está programado, y cómo lo deshago?"
//
// Programar un saludo escribe DOS cosas: la fila en la cola de envíos y la
// marca de equipo que pone la tarjeta en verde. Cancelar la fila siempre fue
// posible; borrar la marca no lo era, así que la tarjeta seguía diciendo
// "✅ Programado" y no había vuelta atrás desde la pantalla que lo mostraba.
// Un saludo programado por error —día equivocado, persona equivocada— se
// quedaba ahí (Lissette 2026-08-17: "por si nos equivocamos").
//
// Deshacer bien son las dos cosas, en este orden: cancelar la fila (que es lo
// que evita el correo) y después quitar la marca (que es lo que devuelve el
// botón a la pantalla). Si la primera falla no se toca la segunda — dejar la
// tarjeta en rojo con el correo todavía en cola sería mentir al revés.
//
// Un CUMPLEAÑOS deja una o dos filas (el titular, y el cónyuge si tiene correo
// propio); un FERIADO deja una POR CLIENTE de la cartera. Por eso el feriado se
// busca por día —no por cliente— y se cancela por tandas, contando lo que no se
// pudo: decir "desprogramado" cuando la mitad sigue en cola sería el mismo
// engaño al revés que ya costó un ticket (Lissette 2026-09-01: "programé un
// feriado y no tengo botón para desprogramar").

import { useCallback, useEffect, useMemo, useState } from 'react';
import { listScheduledSends, cancelScheduledSend, undoTeamAction } from './api.js';
import { bdayGreetingKey, localDayKey } from './greetings.js';

/** Índice de la cola: `<clientId>|<source>|<día local>` → filas pendientes. */
const indexRows = (rows) => {
  const map = new Map();
  for (const r of (Array.isArray(rows) ? rows : [])) {
    if (r.status !== 'pending' || !r.clientId) continue;
    const k = `${r.clientId}|${r.source}|${localDayKey(new Date(r.runAt))}`;
    const list = map.get(k);
    if (list) list.push(r); else map.set(k, [r]);
  }
  return map;
};

/** Índice del feriado: `<día local>` → TODAS sus filas, de todos los clientes. */
const indexHolidays = (rows) => {
  const map = new Map();
  for (const r of (Array.isArray(rows) ? rows : [])) {
    if (r.status !== 'pending' || r.source !== 'holiday_greeting') continue;
    const k = localDayKey(new Date(r.runAt));
    if (!k) continue;
    const list = map.get(k);
    if (list) list.push(r); else map.set(k, [r]);
  }
  return map;
};

/**
 * La cola de saludos PENDIENTES de la agencia, indexada para preguntarle a una
 * tarjeta "¿lo tuyo está programado?".
 *
 * Solo pendientes: lo ya enviado no se puede desprogramar, y mostrar el botón
 * para algo que ya salió sería peor que no mostrarlo.
 */
export function useGreetingQueue({ enabled = true } = {}) {
  const [pending, setPending] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(() => {
    if (!enabled) return Promise.resolve();
    return Promise.all([
      listScheduledSends({ status: 'pending', source: 'birthday_greeting', scope: 'agency' }),
      listScheduledSends({ status: 'pending', source: 'holiday_greeting', scope: 'agency' }),
    ])
      .then(([b, h]) => { setPending([...(b || []), ...(h || [])]); setLoaded(true); })
      // Best-effort: sin cola, las tarjetas siguen funcionando como antes —
      // solo no ofrecen desprogramar.
      .catch(() => { setLoaded(true); });
  }, [enabled]);

  useEffect(() => { let alive = true; reload().then(() => { if (!alive) return; }); return () => { alive = false; }; }, [reload]);

  const byKey = useMemo(() => indexRows(pending), [pending]);
  const holByDay = useMemo(() => indexHolidays(pending), [pending]);

  /** Filas pendientes de ese cliente, ese tipo y ese día. */
  const find = useCallback(
    (clientId, source, dayKey) => (clientId && dayKey ? byKey.get(`${clientId}|${source}|${dayKey}`) || [] : []),
    [byKey],
  );

  /**
   * Filas pendientes del saludo de feriado de ese día — la cartera entera.
   * Por día y no por cliente: el saludo se encola uno por cliente, y lo que la
   * pantalla enseña (y hay que deshacer) es el feriado completo.
   */
  const findHoliday = useCallback(
    (dayKey) => (dayKey ? holByDay.get(dayKey) || [] : []),
    [holByDay],
  );

  /**
   * Deshacer: cancela las filas en cola y borra la marca que pinta el verde.
   * La marca es `markerKey` si se da (feriados) o la del cumpleaños que forman
   * `personKey` + `dayKey` (crm-app/greetings.js).
   *
   * Devuelve `{ cancelled, failed }`. Solo se quita la marca cuando NO quedó
   * ninguna fila sin cancelar: con correos todavía en cola, la tarjeta tiene
   * que seguir en verde.
   */
  const unschedule = useCallback(async ({ rows, personKey, dayKey, markerKey }) => {
    const ids = (rows || []).map((r) => r && r.id).filter(Boolean);
    let cancelled = 0;
    let failed = 0;
    let firstErr = null;
    // De cinco en cinco: no hay ruta de lote para cancelar, y un feriado son
    // cientos de filas — mandarlas todas a la vez se come el límite de
    // peticiones y falla la mitad sin decir cuál (mismo criterio que la
    // cancelación múltiple de "Correos programados").
    for (let i = 0; i < ids.length; i += 5) {
      const tanda = ids.slice(i, i + 5);
      const res = await Promise.all(tanda.map((id) => cancelScheduledSend(id)
        .then(() => true)
        .catch((e) => { firstErr = firstErr || e; return false; })));
      res.forEach((ok) => { if (ok) cancelled++; else failed++; });
    }
    // Ni una sola: eso es un fallo, no un "desprogramado" a medias.
    if (ids.length && !cancelled) throw (firstErr || new Error('No se pudo cancelar ningún envío.'));

    const key = markerKey || (personKey ? bdayGreetingKey(personKey, dayKey) : null);
    if (key && !failed) {
      // La marca es lo último: un fallo aquí deja el correo ya cancelado (que
      // es lo que importa) y la tarjeta se corrige al recargar.
      try { await undoTeamAction(key); } catch (_) { /* no fatal */ }
    }
    await reload();
    return { cancelled, failed };
  }, [reload]);

  return { find, findHoliday, unschedule, reload, loaded };
}
